import InfoIcon from '@mui/icons-material/Info';
import { Box, IconButton, Modal, Paper, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import { selectBatchId } from '@verantos-dev/feature-supervisor-queue';
import { Button, Icon } from '@verantos-dev/shared-components';

import { useResolveSupervisorConflictMutation } from '../../../../api/supervisorApiSlice';
import { setConflictAcceptError } from '../../../../state/supervisorSlice';

interface AcceptAnnotationModalProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  conflictId: string;
  annotationIdToAccept: string;
  annotationIdToReject: string;
  annotatorNumber?: number | null;
  annotatorNumberToReject?: number | null;
}

export const AcceptAnnotationModal = ({
  showModal,
  setShowModal,
  conflictId,
  annotationIdToAccept,
  annotationIdToReject,
  annotatorNumber = null,
  annotatorNumberToReject = null,
}: AcceptAnnotationModalProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { encounterId } = useParams();
  const batchId = useAppSelector(selectBatchId);

  const [resolveSupervisorConflict, { isLoading: isResolveSupervisorConflictLoading }] =
    useResolveSupervisorConflictMutation();

  const handleAcceptAnnotation = async () => {
    if (!annotationIdToAccept) return;
    try {
      dispatch(setConflictAcceptError({ conflictId: conflictId, error: null }));
      await resolveSupervisorConflict({
        encounterId: encounterId as string,
        batchId: batchId as string,
        annotationIdToAccept: annotationIdToAccept,
        annotationIdToReject: annotationIdToReject,
      }).unwrap();
    } catch (err) {
      console.error('Failed to resolve conflict:', err);
      const errMsg =
        (err as any)?.data?.detail ||
        (err as any)?.data?.message ||
        (err as any)?.data?.errorMessage ||
        (err as any)?.message ||
        formatMessage({ id: 'supervisorReview.conflictTab.error.acceptFailed' });
      dispatch(
        setConflictAcceptError({
          conflictId: conflictId,
          error: errMsg,
        })
      );
    }
  };

  const dispatch = useAppDispatch();
  return (
    <Modal
      open={showModal}
      onClose={() => setShowModal(false)}
      aria-labelledby="finalized-annotation-modal-title"
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          p: theme.spacing(6),
          outline: 'none',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography id="confirm-accept-annotation-modal-title" variant="h6">
              {formatMessage({ id: 'supervisorReview.acceptAnnotationModal.title' })}
            </Typography>
            <IconButton onClick={() => setShowModal(false)} size="small">
              <Icon id="confirm-accept-annotation-modal-close-icon" icon="clear" width={16} />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(4),
              border: `1px solid ${theme.palette.grey[300]}`,
            }}
          >
            <Box sx={{ display: 'flex', p: theme.spacing(4) }}>
              <InfoIcon sx={{ color: theme.palette.primary.main, mr: theme.spacing(2) }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {formatMessage({ id: 'supervisorReview.acceptAnnotationModal.infoBox.title' })}
                </Typography>
                <Box sx={{ display: 'flex', mt: theme.spacing(3), gap: theme.spacing(1) }}>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    {annotationIdToReject
                      ? formatMessage(
                          {
                            id: 'supervisorReview.acceptAnnotationModal.acceptAndDReject.description.annotator.message',
                          },
                          {
                            annotatorNumberToAccept: annotatorNumber,
                            annotatorNumberToReject: annotatorNumberToReject,
                          }
                        )
                      : annotatorNumber
                      ? formatMessage(
                          {
                            id: 'supervisorReview.acceptAnnotationModal.description.annotator.message',
                          },
                          { annotatorNumber: annotatorNumber }
                        )
                      : formatMessage({
                          id: 'supervisorReview.acceptAnnotationModal.description.message',
                        })}
                  </Typography>
                </Box>
                {isResolveSupervisorConflictLoading && (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {formatMessage({
                      id: 'supervisorReview.acceptAnnotationModal.acceptLoading.message',
                    })}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: theme.spacing(3),
              justifyContent: 'flex-end',
            }}
          >
            <Button
              id="cancel-accept-annotation-button"
              displayLabel={formatMessage({
                id: 'supervisorReview.acceptAnnotationModal.cancel.button',
              })}
              variant="text"
              sx={{ fontSize: '15px', textTransform: 'none' }}
              onClickHandler={() => setShowModal(false)}
              disabled={isResolveSupervisorConflictLoading}
            />
            <Button
              id="confirm-accept-annotation-button"
              displayLabel={
                annotatorNumber
                  ? formatMessage(
                      { id: 'supervisorReview.acceptAnnotationModal.acceptAnnotator.button' },
                      { annotatorNumber: annotatorNumber }
                    )
                  : formatMessage({ id: 'supervisorReview.acceptAnnotationModal.accept.button' })
              }
              variant="contained"
              sx={{
                fontSize: '15px',
                textTransform: 'none',
                backgroundColor: theme.palette.primary.dark,
              }}
              onClickHandler={handleAcceptAnnotation}
              disabled={isResolveSupervisorConflictLoading}
            />
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};
